import { listPrescriptions } from "@/core/clinical/clinical-store";
import { getPatientById } from "@/core/patients/patient-store";
import { ensureCoverageCaseForPrescription } from "./coverage-case-flow";
import { applyCoverageCaseReviewSideEffects } from "./coverage-case-review-effects";
import { getInsurerById } from "./insurer-store";
import type { CoverageCase, CoverageCaseInput, CoverageCaseStatus } from "./types";
import { readJson, writeJson, getStorageItem, setStorageItem } from "@/core/tenant/hospitol-storage";

const CASES_KEY = "hospitol.coverageCases";

function readJson<T>(key: string, fallback: T): T {
  try {
    const raw = getStorageItem(key);
    return raw ? (JSON.parse(raw) as T) : fallback;
  } catch {
    return fallback;
  }
}

function writeCases(list: CoverageCase[]): void {
  setStorageItem(CASES_KEY, JSON.stringify(list));
  window.dispatchEvent(new CustomEvent("hospitol:coverage-cases"));
}

function normalizeCase(raw: Record<string, unknown>): CoverageCase {
  return {
    id: String(raw.id ?? ""),
    insurerId: String(raw.insurerId ?? ""),
    agentId: raw.agentId ? String(raw.agentId) : null,
    patientId: String(raw.patientId ?? ""),
    admissionId: raw.admissionId ? String(raw.admissionId) : null,
    prescriptionId: raw.prescriptionId ? String(raw.prescriptionId) : null,
    labOrderId: raw.labOrderId ? String(raw.labOrderId) : null,
    imagingExamId: raw.imagingExamId ? String(raw.imagingExamId) : null,
    billingDocumentId: raw.billingDocumentId ? String(raw.billingDocumentId) : null,
    kind: (raw.kind as CoverageCase["kind"]) ?? "consultation",
    status: (raw.status as CoverageCaseStatus) ?? "pending",
    label: String(raw.label ?? ""),
    amount: Number(raw.amount ?? 0),
    memberNumber: String(raw.memberNumber ?? ""),
    policyNumber: String(raw.policyNumber ?? ""),
    rejectionReason: String(raw.rejectionReason ?? ""),
    reviewedAt: raw.reviewedAt ? String(raw.reviewedAt) : null,
    reviewedBy: String(raw.reviewedBy ?? ""),
    createdAt: String(raw.createdAt ?? new Date().toISOString()),
    updatedAt: String(raw.updatedAt ?? new Date().toISOString()),
    hubSubmissionId: raw.hubSubmissionId ? String(raw.hubSubmissionId) : null,
    hubSubmittedAt: raw.hubSubmittedAt ? String(raw.hubSubmittedAt) : null,
  };
}

function readCases(): CoverageCase[] {
  return readJson<Record<string, unknown>[]>(CASES_KEY, []).map(normalizeCase);
}

export function listCoverageCases(filter?: {
  insurerId?: string;
  patientId?: string;
  status?: CoverageCaseStatus | CoverageCaseStatus[];
}): CoverageCase[] {
  let list = readCases();
  if (filter?.insurerId) list = list.filter((c) => c.insurerId === filter.insurerId);
  if (filter?.patientId) list = list.filter((c) => c.patientId === filter.patientId);
  if (filter?.status) {
    const statuses = Array.isArray(filter.status) ? filter.status : [filter.status];
    list = list.filter((c) => statuses.includes(c.status));
  }
  return list.sort(
    (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
  );
}

export function getCoverageCase(id: string): CoverageCase | null {
  return readCases().find((c) => c.id === id) ?? null;
}

export function createCoverageCase(input: CoverageCaseInput): CoverageCase | null {
  if (!getInsurerById(input.insurerId)) return null;
  const patient = getPatientById(input.patientId);
  if (!patient) return null;

  const now = new Date().toISOString();
  const coverageCase: CoverageCase = {
    id: `cc-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
    insurerId: input.insurerId,
    agentId: input.agentId ?? null,
    patientId: input.patientId,
    admissionId: input.admissionId ?? null,
    prescriptionId: input.prescriptionId ?? null,
    labOrderId: input.labOrderId ?? null,
    imagingExamId: input.imagingExamId ?? null,
    billingDocumentId: input.billingDocumentId ?? null,
    kind: input.kind,
    status: "pending",
    label: input.label.trim(),
    amount: input.amount ?? 0,
    memberNumber: input.memberNumber?.trim() || patient.memberNumber || "",
    policyNumber: input.policyNumber?.trim() || patient.policyNumber || "",
    rejectionReason: "",
    reviewedAt: null,
    reviewedBy: "",
    createdAt: now,
    updatedAt: now,
    hubSubmissionId: null,
    hubSubmittedAt: null,
  };
  const list = readCases();
  list.unshift(coverageCase);
  writeCases(list);
  return coverageCase;
}

export function reviewCoverageCase(
  id: string,
  status: Extract<CoverageCaseStatus, "approved" | "rejected" | "partial">,
  reviewedBy: string,
  rejectionReason = "",
): CoverageCase | null {
  const list = readCases();
  const index = list.findIndex((c) => c.id === id);
  if (index < 0) return null;
  const now = new Date().toISOString();
  list[index] = {
    ...list[index]!,
    status,
    rejectionReason: status === "rejected" ? rejectionReason.trim() : "",
    reviewedAt: now,
    reviewedBy: reviewedBy.trim(),
    updatedAt: now,
  };
  writeCases(list);
  const updated = list[index]!;
  applyCoverageCaseReviewSideEffects(updated);
  return updated;
}

/** Synchronise les dossiers assureur — uniquement si les règles du contrat l'exigent. */
export function syncCoverageCasesFromPrescriptions(): void {
  const prescriptions = listPrescriptions().filter(
    (rx) => rx.status !== "draft" && rx.lines.length > 0,
  );
  for (const rx of prescriptions) {
    ensureCoverageCaseForPrescription(rx.id);
  }
}

export function countPendingCases(insurerId?: string): number {
  return listCoverageCases({
    insurerId,
    status: "pending",
  }).length;
}

export function coverageCasePath(id: string): string {
  return `/tenant/hospitol/insurance/cases/${id}`;
}

export function markCoverageCaseSubmittedToHub(
  id: string,
  hubSubmissionId: string,
): CoverageCase | null {
  const list = readCases();
  const index = list.findIndex((c) => c.id === id);
  if (index < 0) return null;
  const now = new Date().toISOString();
  list[index] = {
    ...list[index]!,
    hubSubmissionId,
    hubSubmittedAt: now,
    updatedAt: now,
  };
  writeCases(list);
  return list[index]!;
}

export function patchCoverageCase(
  id: string,
  patch: Partial<Pick<CoverageCase, "labOrderId" | "imagingExamId" | "billingDocumentId">>,
): CoverageCase | null {
  const list = readCases();
  const index = list.findIndex((c) => c.id === id);
  if (index < 0) return null;
  list[index] = { ...list[index]!, ...patch, updatedAt: new Date().toISOString() };
  writeCases(list);
  return list[index]!;
}
